import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../../styles/ProductListBlock.css";
import "../../styles/CatalogProductGrid.css";
import CatalogPagination from "./CatalogPagination";

function getTotalPages(totalItems, perPage) {
  const t = Number(totalItems || 0);
  const p = Number(perPage || 1);
  return Math.max(1, Math.ceil(t / p));
}

export default function CatalogProductGrid({
  title,
  products = [],
  onAction,
  perPage = 4,
}) {
  const { t, i18n } = useTranslation("catalog");
  const [page, setPage] = useState(1);

  function formatUAH(price) {
    const n = Number(String(price ?? 0).replace(",", "."));
    const locale = i18n.language?.startsWith("uk") ? "uk-UA" : "en-US";

    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);

    return `${t("currency_symbol")}${formatted}`;
  }

  const totalPages = useMemo(
    () => getTotalPages(Array.isArray(products) ? products.length : 0, perPage),
    [products, perPage]
  );

  useEffect(() => {
    setPage(1);
  }, [products]);

  const pageItems = useMemo(() => {
    const arr = Array.isArray(products) ? products : [];
    const start = (page - 1) * perPage;
    return arr.slice(start, start + perPage);
  }, [products, page, perPage]);

  function openProduct(p) {
    onAction?.({
      type: "open_product",
      product_id: p.variant_id || p.id,
      product: p,
    });
  }

  function addToCart(p) {
    onAction?.({
      type: "add_to_cart",
      product_id: p.variant_id || p.id,
      product: { ...p, id: p.variant_id || p.id },
      qty: 1,
    });
  }

  return (
    <div className="catBlock">
      <div className="catBlockTitle"></div>

      <div className="catGrid" role="list" aria-label={t("products_aria")}>
        {pageItems.map((p) => {
          const imgSrc = p.image_url || p.image_proxy_url || "";
          const hasOld =
            p.old_price != null &&
            Number(String(p.old_price).replace(",", ".")) > 0 &&
            Number(String(p.price).replace(",", ".")) > 0 &&
            Number(String(p.old_price).replace(",", ".")) >
              Number(String(p.price).replace(",", "."));

          const productId = p.variant_id || p.id;
          if (!productId) return null;

          return (
            <div key={productId} className="pCard catCard" role="listitem">
              <div className="pImgWrap">
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt={p.name || t("fallback_product")}
                    loading="lazy"
                    className="pImg"
                    onClick={() => openProduct(p)}
                    style={{ cursor: "pointer" }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="pImgFallback">{t("no_image")}</div>
                )}
              </div>

              <button
                type="button"
                className="pNameBtn"
                title={p.name || t("fallback_product")}
                onClick={() => openProduct(p)}
              >
                {p.name || t("fallback_product")}
              </button>

              <div className="pPriceRow">
                <div className="pPrice">{formatUAH(p.price)}</div>
                {hasOld && (
                  <div className="pOldPrice" aria-label={t("old_price")}>
                    {formatUAH(p.old_price)}
                  </div>
                )}
              </div>

              <button className="pBtn" onClick={() => addToCart(p)}>
                {t("add_to_cart")}
              </button>
            </div>
          );
        })}
      </div>

      <CatalogPagination
        page={page}
        totalPages={totalPages}
        onPageChange={(n) => setPage(n)}
        onMore={() => setPage((p) => Math.min(totalPages, p + 1))}
        moreDisabled={page >= totalPages}
        showMore={true}
      />
    </div>
  );
}